import mixitup from 'mixitup';

/**
 * Initializes MixItUp filtering and interaction logic
 * for the "Latest Posts B1" block instance.
 *
 * This function is intentionally verbose and defensive:
 * - Every DOM lookup is scoped to the block
 * - Every interaction is documented
 * - State is tracked explicitly
 *
 * @author Dreamers of Day
 * @param {HTMLElement} block - Root block element for the module instance
 * @returns {void}
 */
function initializeLatestPostsBlock(block) {
    // ---------------------------------------------------------------------
    // DOM REFERENCES
    // ---------------------------------------------------------------------

    // Container that holds all post cards for MixItUp
    const postsContainer = block.querySelector(
        '.o-latest-posts-b-1__posts-container',
    );

    // Wrapper element that contains all filter buttons
    const controlsContainer = block.querySelector('.controls');

    // Convert NodeList of post cards into an array for easier iteration
    const postCards = Array.from(
        postsContainer.querySelectorAll('.js-post-card'),
    );

    // Convert NodeList of filter buttons into an array
    const filterButtons = Array.from(
        block.querySelectorAll('.o-latest-posts-b-1__filter-button'),
    );

    // ---------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------

    // Tracks the currently active post ID (if any)
    let activePostId = null;

    // ---------------------------------------------------------------------
    // DEFAULT FILTER SETUP
    // ---------------------------------------------------------------------

    // Attempt to find a default filter button
    const defaultFilterButton = controlsContainer.querySelector('.filter');

    // Safely extract the default filter selector
    const defaultFilter =
        defaultFilterButton?.getAttribute('data-filter') || '*';

    // ---------------------------------------------------------------------
    // MIXITUP INITIALIZATION
    // ---------------------------------------------------------------------

    // Initialize MixItUp with custom animation and disabled controls
    const mixer = mixitup(postsContainer, {
        animation: {
            enable: true,
            effects: 'fade scale',
            effectsIn: '',
            effectsOut: '',
            duration: 600,
            easing: 'ease',
            applyPerspective: true,
            perspectiveDistance: '2000px',
            perspectiveOrigin: '50% 50%',
            queue: true,
            animateResizeContainer: true,
            animateResizeTargets: false,
            reverseOut: false,
            nudge: true,
        },
        controls: {
            enable: false,
        },
        load: {
            filter: defaultFilter,
        },
    });

    // ---------------------------------------------------------------------
    // INITIAL LAYOUT STATE
    // ---------------------------------------------------------------------

    // Ensure initial layout mode matches default filter
    if (defaultFilter !== '*') {
        postsContainer.classList.add(
            'o-latest-posts-b-1__posts-container--uniform',
        );
    }

    // ---------------------------------------------------------------------
    // FILTER HANDLING
    // ---------------------------------------------------------------------

    /**
     * Handles click events on filter buttons using event delegation.
     *
     * - Determines the clicked filter button
     * - Updates active button UI state
     * - Applies MixItUp filtering
     *
     * @author Dreamers of Day
     * @param {MouseEvent} event - Click event from the controls container
     * @returns {void}
     */
    function handleFilterClick(event) {
        // Find the closest filter button ancestor
        const clickedButton = event.target.closest('.filter');

        // Exit early if the click did not originate from a filter button
        if (!clickedButton) {
            return;
        }

        // Extract filter selector from data attribute
        const filterSelector = clickedButton.getAttribute('data-filter');

        // -----------------------------------------------------------------
        // UPDATE ACTIVE BUTTON STATE
        // -----------------------------------------------------------------

        // Remove active class from all filter buttons
        filterButtons.forEach((button) => {
            button.classList.remove(
                'o-latest-posts-b-1__filter-button--active',
            );
        });

        // Apply active class to the clicked button
        clickedButton.classList.add(
            'o-latest-posts-b-1__filter-button--active',
        );

        // -----------------------------------------------------------------
        // UPDATE LAYOUT MODE
        // -----------------------------------------------------------------

        // Enable uniform layout for any filtered state except "All"
        // if (filterSelector === '*') {
        //     postsContainer.classList.remove(
        //         'o-latest-posts-b-1__posts-container--uniform',
        //     );
        // } else {
        //     postsContainer.classList.add(
        //         'o-latest-posts-b-1__posts-container--uniform',
        //     );
        // }

        // -----------------------------------------------------------------
        // APPLY MIXITUP FILTER
        // -----------------------------------------------------------------

        // Reset filtering first, then apply new filter
        mixer.filter(filterSelector);
    }

    // ---------------------------------------------------------------------
    // POST CARD STATE MANAGEMENT
    // ---------------------------------------------------------------------

    /**
     * Updates the currently active post by ID.
     *
     * @author Dreamers of Day
     * @param {string|null} postId - Post ID from data attribute
     * @returns {void}
     */
    function updateActivePostById(postId) {
        // If no post ID is provided, reset state
        if (!postId) {
            activePostId = null;
            return;
        }

        // Find the matching post card by numeric comparison
        const matchingPostCard = postCards.find((card) => {
            return Number(card.dataset.postId) === Number(postId);
        });

        // Persist active post ID
        activePostId = postId;

        // Highlight the active post card
        highlightActivePostCard(matchingPostCard);
    }

    /**
     * Applies active styling to the selected post card
     * and removes it from all others.
     *
     * @author Dreamers of Day
     * @param {HTMLElement} activeCard - Post card element to activate
     * @returns {void}
     */
    function highlightActivePostCard(activeCard) {
        // Iterate over every post card
        postCards.forEach((card) => {
            // Toggle the `active` class based on identity match
            card.classList.toggle('active', card === activeCard);
        });
    }

    // ---------------------------------------------------------------------
    // EVENT BINDINGS
    // ---------------------------------------------------------------------

    // Attach delegated click handler for filter buttons
    controlsContainer.addEventListener('click', handleFilterClick);

    // Attach click handler to each individual post card
    postCards.forEach((postCard) => {
        postCard.addEventListener('click', (event) => {
            // Prevent default link navigation behavior
            event.preventDefault();

            // Update active post using data attribute
            updateActivePostById(postCard.dataset?.postId || null);
        });
    });
}

/**
 * Entry point for the Latest Posts B1 module.
 *
 * Locates all matching blocks on the page and
 * initializes each independently.
 *
 * @author Dreamers of Day
 * @returns {void}
 */
export default function latestPostsB1() {
    // Query all module instances on the page
    const blocks = document.querySelectorAll("[data-module='latestPostsB1']");

    // Initialize each block independently
    blocks.forEach((block) => {
        initializeLatestPostsBlock(block);
    });
}
